import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Building2, Shield, DollarSign, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const demoLenders = [
  {
    id: "1",
    institution_name: "GhanaFirst Finance",
    license_number: "LIC-2024-001",
    total_loans_funded: 1250,
    risk_appetite: "conservative",
    status: "active"
  },
  {
    id: "2",
    institution_name: "Accra Capital Partners",
    license_number: "LIC-2024-002",
    total_loans_funded: 850,
    risk_appetite: "moderate",
    status: "active"
  },
  {
    id: "3",
    institution_name: "West African Growth Fund",
    license_number: "LIC-2024-003",
    total_loans_funded: 2100,
    risk_appetite: "aggressive",
    status: "active"
  },
  {
    id: "4",
    institution_name: "Kumasi Investment Trust",
    license_number: "LIC-2024-004",
    total_loans_funded: 1500,
    risk_appetite: "moderate",
    status: "active"
  },
  {
    id: "5",
    institution_name: "Coastal Credit Union",
    license_number: "LIC-2024-005",
    total_loans_funded: 750,
    risk_appetite: "conservative",
    status: "active"
  }
];

const LenderList = () => {
  const { toast } = useToast();
  const { data: lenders, isLoading, error } = useQuery({
    queryKey: ["lenders-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lenders")
        .select("*")
        .order("total_loans_funded", { ascending: false });

      if (error) {
        console.error("Error fetching lenders:", error);
        toast({
          title: "Error",
          description: "Failed to load lender list",
          variant: "destructive",
        });
        throw error;
      }
      
      // Return demo data if no real data is available
      return data?.length ? data : demoLenders;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center h-40 space-y-4">
            <p className="text-red-500">Failed to load lender list</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!lenders?.length) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center h-40 space-y-4">
            <p className="text-muted-foreground">No lenders found</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {lenders?.map((lender) => (
        <Card key={lender.id} className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="h-6 w-6 text-primary" />
                <h3 className="text-lg font-medium">{lender.institution_name}</h3>
              </div>
              {lender.license_number && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-4 w-4" />
                  <span>License: {lender.license_number}</span>
                </div>
              )}
            </div>
            
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Loans Funded</p>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <p className="text-2xl font-bold">{lender.total_loans_funded || 0}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Risk Appetite</p>
                <p className="text-2xl font-bold capitalize">{lender.risk_appetite || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${lender.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`} />
                  <p className="font-medium capitalize">{lender.status || "Inactive"}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default LenderList;